import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { Session } from '@supabase/supabase-js';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { AlertCircle, ChevronLeft, Clock3, FileText, Paperclip, Send } from 'lucide-react';
import { getOrCreateSupportSessionId, parseSupportTicketMessage } from '../services/supportSession';

type Ticket = {
    id: string;
    name: string;
    email: string;
    plan: string;
    category: string;
    status: 'open' | 'closed' | 'resolved';
    created_at: string;
    updated_at: string;
};

type Message = {
    id: string;
    ticket_id: string;
    sender_type: 'user' | 'admin';
    content: string | null;
    attachment_url: string | null;
    created_at: string;
};

const isImageAttachment = (attachmentUrl: string) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(attachmentUrl);

export const SupportTicketPage: React.FC = () => {
    const navigate = useNavigate();
    const { ticketId } = useParams<{ ticketId: string }>();
    const [session, setSession] = useState<Session | null>(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [localSessionId, setLocalSessionId] = useState('');
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sessionId = getOrCreateSupportSessionId();
        setLocalSessionId(sessionId);

        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
            setSession(currentSession);
            setAuthResolved(true);
        });

        const {
            data: { subscription }
        } = supabase.auth.onAuthStateChange((_event, currentSession) => {
            setSession(currentSession);
            setAuthResolved(true);
        });

        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (!ticketId || !authResolved || !localSessionId) {
            return;
        }

        const loadThread = async () => {
            setLoading(true);
            setError(null);

            let ticketQuery = supabase
                .from('support_tickets')
                .select('id, name, email, plan, category, status, created_at, updated_at')
                .eq('id', ticketId);

            if (session?.user?.id) {
                ticketQuery = ticketQuery.eq('user_id', session.user.id);
            } else {
                ticketQuery = ticketQuery.eq('session_id', localSessionId);
            }

            const { data: ticketData, error: ticketError } = await ticketQuery.single();

            if (ticketError || !ticketData) {
                setTicket(null);
                setMessages([]);
                setError('This ticket could not be found, or you do not have access to it.');
                setLoading(false);
                return;
            }

            const { data: messageData, error: messageError } = await supabase
                .from('support_messages')
                .select('id, ticket_id, sender_type, content, attachment_url, created_at')
                .eq('ticket_id', ticketId)
                .order('created_at', { ascending: true });

            if (messageError) {
                setTicket(ticketData);
                setMessages([]);
                setError('The ticket loaded, but the conversation history could not be retrieved.');
                setLoading(false);
                return;
            }

            setTicket(ticketData);
            setMessages(messageData || []);
            setLoading(false);
        };

        loadThread();
    }, [ticketId, authResolved, localSessionId, session?.user?.id]);

    useEffect(() => {
        if (!ticketId) {
            return;
        }

        const channel = supabase
            .channel(`support_ticket_${ticketId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `ticket_id=eq.${ticketId}` },
                (payload) => {
                    const nextMessage = payload.new as Message;
                    setMessages((currentMessages) => (
                        currentMessages.some((message) => message.id === nextMessage.id)
                            ? currentMessages
                            : [...currentMessages, nextMessage]
                    ));
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'support_tickets', filter: `id=eq.${ticketId}` },
                (payload) => {
                    const updatedTicket = payload.new as Ticket;
                    setTicket((currentTicket) => currentTicket ? { ...currentTicket, ...updatedTicket } : currentTicket);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [ticketId]);

    useEffect(() => {
        if (messages.length === 0) {
            return;
        }

        const timeout = window.setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);

        return () => window.clearTimeout(timeout);
    }, [messages]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!ticketId || !ticket || ticket.status !== 'open' || !newMessage.trim() || sending) {
            return;
        }

        const messageContent = newMessage.trim();
        setSending(true);
        setError(null);
        setNewMessage('');

        const { error: insertError } = await supabase
            .from('support_messages')
            .insert({
                ticket_id: ticketId,
                sender_type: 'user',
                content: messageContent
            });

        if (insertError) {
            console.error('Error sending support message:', insertError);
            setNewMessage(messageContent);
            setError('Your reply could not be sent. Please try again.');
        }

        setSending(false);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file || !ticketId || !ticket || ticket.status !== 'open') {
            return;
        }

        setIsUploading(true);
        setError(null);

        const fileExt = file.name.split('.').pop();
        const uploadId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : Date.now().toString(36);
        const fileName = `${ticketId}/${uploadId}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
            .from('support-attachments')
            .upload(fileName, file);

        if (uploadError || !uploadData) {
            console.error('Error uploading support attachment:', uploadError);
            setError('The attachment could not be uploaded. Please try again.');
            setIsUploading(false);
            return;
        }

        const { data: { publicUrl } } = supabase.storage
            .from('support-attachments')
            .getPublicUrl(fileName);

        const { error: messageError } = await supabase
            .from('support_messages')
            .insert({
                ticket_id: ticketId,
                sender_type: 'user',
                content: `Attachment: ${file.name}`,
                attachment_url: publicUrl
            });

        if (messageError) {
            console.error('Error saving support attachment message:', messageError);
            setError('The file uploaded, but it could not be attached to the conversation.');
        }

        e.target.value = '';
        setIsUploading(false);
    };

    const firstMessage = messages[0];
    const parsedFirstMessage = parseSupportTicketMessage(firstMessage?.content);
    const ticketSubject = parsedFirstMessage.subject || (ticket ? `${ticket.category} Ticket` : 'Support Ticket');
    const ticketOpen = ticket?.status === 'open';

    return (
        <div className="min-h-screen bg-[#060810] pt-24 pb-12 relative overflow-hidden">
            <Helmet>
                <link rel="canonical" href={`https://www.forexfundai.com/support/ticket/${ticketId}`} />
            </Helmet>
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#D4A537]/5 blur-[120px] rounded-full pointer-events-none"></div>
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full pointer-events-none"></div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <button
                    type="button"
                    onClick={() => navigate('/support')}
                    className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Back To Support Center
                </button>

                {loading ? (
                    <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-white/5 p-10 text-center text-gray-400">
                        Loading conversation...
                    </div>
                ) : error && !ticket ? (
                    <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-red-500/20 p-10 text-center">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-red-500/10 text-red-400 flex items-center justify-center mb-6">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-black text-white mb-3">Ticket Unavailable</h1>
                        <p className="text-gray-400 max-w-xl mx-auto">{error}</p>
                    </div>
                ) : ticket ? (
                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
                        <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-white/5 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden">
                            <div className="px-6 md:px-8 py-6 border-b border-white/5 bg-[#0c101a]/70">
                                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                    <div>
                                        <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-[#D4A537]/20 rounded-full bg-[#D4A537]/5">
                                            <FileText className="w-4 h-4 text-[#D4A537]" />
                                            <span className="text-[11px] font-black tracking-[2px] uppercase text-[#D4A537]">Ticket Thread</span>
                                        </div>
                                        <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">{ticketSubject}</h1>
                                        <p className="text-gray-400 mt-3 max-w-3xl">
                                            {parsedFirstMessage.body || 'Open the full conversation below and continue the thread with our support team.'}
                                        </p>
                                    </div>

                                    <div className={`shrink-0 text-[11px] font-black uppercase tracking-[2px] px-3 py-1.5 rounded-full border ${ticket.status === 'open'
                                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20'
                                        : 'bg-gray-800 text-gray-300 border-gray-700'
                                        }`}>
                                        {ticket.status}
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 md:p-8">
                                {error && (
                                    <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                                        {error}
                                    </div>
                                )}

                                <div className="min-h-[420px] max-h-[65vh] overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-4">
                                    {messages.length === 0 ? (
                                        <div className="flex items-center justify-center min-h-[320px] text-sm text-gray-500">
                                            No conversation messages are available for this ticket yet.
                                        </div>
                                    ) : (
                                        messages.map((message, index) => {
                                            const parsedMessage = index === 0
                                                ? parseSupportTicketMessage(message.content)
                                                : { subject: null, body: (message.content || '').trim() };
                                            const messageBody = parsedMessage.body || message.content || '';

                                            return (
                                                <div
                                                    key={message.id}
                                                    className={`flex flex-col max-w-[85%] ${message.sender_type === 'user' ? 'self-end' : 'self-start'}`}
                                                >
                                                    <div className={`rounded-3xl px-4 py-3 ${message.sender_type === 'user'
                                                        ? 'bg-[#D4A537] text-black rounded-br-md'
                                                        : 'bg-[#0b0f19] border border-white/5 text-gray-100 rounded-bl-md'
                                                        }`}>
                                                        {index === 0 && parsedMessage.subject && (
                                                            <div className={`text-[11px] uppercase tracking-[2px] font-black mb-2 ${message.sender_type === 'user' ? 'text-black/60' : 'text-[#D4A537]'}`}>
                                                                {parsedMessage.subject}
                                                            </div>
                                                        )}
                                                        {messageBody && <p className="text-sm whitespace-pre-wrap leading-6">{messageBody}</p>}
                                                        {message.attachment_url && (
                                                            isImageAttachment(message.attachment_url) ? (
                                                                <a href={message.attachment_url} target="_blank" rel="noopener noreferrer" className="block mt-3">
                                                                    <img src={message.attachment_url} alt="Support attachment" className="max-w-full rounded-2xl border border-black/10" />
                                                                </a>
                                                            ) : (
                                                                <a
                                                                    href={message.attachment_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={`inline-flex items-center gap-2 mt-3 text-sm font-semibold ${message.sender_type === 'user' ? 'text-black/70' : 'text-[#D4A537]'}`}
                                                                >
                                                                    <Paperclip className="w-4 h-4" />
                                                                    Open Attachment
                                                                </a>
                                                            )
                                                        )}
                                                    </div>
                                                    <span className={`mt-1 text-[11px] text-gray-500 ${message.sender_type === 'user' ? 'text-right' : 'text-left'}`}>
                                                        {new Date(message.created_at).toLocaleString([], {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            <div className="p-4 md:p-6 border-t border-white/5 bg-[#0c101a]/70">
                                <form onSubmit={handleSendMessage} className="flex items-end gap-3">
                                    <label className={`p-3 rounded-2xl border transition-colors shrink-0 ${ticketOpen ? 'border-white/10 bg-[#060810]/70 text-gray-400 hover:text-white cursor-pointer' : 'border-white/5 bg-[#060810]/40 text-gray-600 cursor-not-allowed'}`}>
                                        <Paperclip className="w-5 h-5" />
                                        <input
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileUpload}
                                            disabled={!ticketOpen || isUploading}
                                            accept="image/*,.pdf,.doc,.docx"
                                        />
                                    </label>

                                    <div className="flex-1 rounded-3xl border border-white/10 bg-[#060810]/70 px-4 py-3 focus-within:border-[#D4A537]/40 transition-colors">
                                        <textarea
                                            rows={3}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder={ticketOpen ? 'Reply to this conversation...' : 'This ticket is closed. Replies are disabled.'}
                                            disabled={!ticketOpen || sending}
                                            className="w-full bg-transparent text-white text-sm outline-none resize-none placeholder:text-gray-600 disabled:opacity-60"
                                        ></textarea>
                                        <div className="mt-3 flex items-center justify-between gap-3">
                                            <span className="text-[11px] text-gray-500">
                                                {isUploading ? 'Uploading attachment...' : ticketOpen ? 'Messages go directly to your support thread.' : 'An analyst must reopen this ticket before new replies can be sent.'}
                                            </span>
                                            <button
                                                type="submit"
                                                disabled={!ticketOpen || sending || !newMessage.trim()}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820] text-black text-xs font-black tracking-[2px] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {sending ? 'Sending...' : 'Send Reply'}
                                                <Send className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <aside className="space-y-6">
                            <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-white/5 p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.45)]">
                                <h2 className="text-sm font-black uppercase tracking-[2px] text-[#D4A537] mb-5">Ticket Overview</h2>
                                <div className="space-y-4 text-sm">
                                    <div>
                                        <div className="text-[11px] font-black uppercase tracking-[2px] text-gray-500 mb-1">Category</div>
                                        <div className="text-white font-semibold">{ticket.category}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-black uppercase tracking-[2px] text-gray-500 mb-1">Plan</div>
                                        <div className="text-white font-semibold">{ticket.plan}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-black uppercase tracking-[2px] text-gray-500 mb-1">Email</div>
                                        <div className="text-white font-semibold break-all">{ticket.email}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-black uppercase tracking-[2px] text-gray-500 mb-1">Created</div>
                                        <div className="text-white font-semibold inline-flex items-center gap-2">
                                            <Clock3 className="w-4 h-4 text-[#D4A537]" />
                                            {new Date(ticket.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-[#131722]/60 backdrop-blur-2xl rounded-3xl border border-white/5 p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.45)]">
                                <h2 className="text-sm font-black uppercase tracking-[2px] text-[#D4A537] mb-4">Conversation Tips</h2>
                                <div className="space-y-3 text-sm text-gray-400 leading-6">
                                    <p>Keep replies in the same thread so your analyst sees the full history.</p>
                                    <p>Attach screenshots, statements, or error captures when they help explain the issue.</p>
                                    <p>If the ticket is closed, reply from the main support form or wait for the analyst to reopen it.</p>
                                </div>
                            </div>
                        </aside>
                    </div>
                ) : null}
            </div>
        </div>
    );
};